/**
 * MobileLeaveFormDrawer
 *
 * Mobile replacement for LeaveFormModal.
 * Uses MUI SwipeableDrawer (bottom sheet) instead of Bootstrap Modal.
 * All logic, state, effects, handlers, and redux interactions are
 * preserved identically from LeaveFormModal.
 *
 * Components from MUI: SwipeableDrawer, Box, Typography, IconButton, Button,
 *   Chip, TextField (multiline), Alert
 * Kept as-is: SelectInput, Langs. Dates: FormDatePicker (MUI X DatePicker)
 */
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// MUI Components
import {
  SwipeableDrawer,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  TextField as MuiTextField,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// App imports (same as LeaveFormModal)
import Langs from "../../../app/lang/langs";
import { SelectInput } from "../../../components/inputs/Select";
import FormDatePicker from "../../../components/inputs/FormDatePicker";
import { getLeaves, requestDeleteLeave, requestLeave, submitLeave } from "../leavesSlice";
import {
  checkPermission,
  generateUniqueId,
  getLastUpdatedFromDate,
  getTagClass,
  isOwner,
  showSuccessToast,
  isAdmin,
  showErrorToast,
} from "../../../app/utils/helpers";
import { isCollabPresent } from "../../../app/utils/helpers";
import { getPlanning } from "../../planning/planningSlice";
import { getCollabs } from "../../../components/collabs/collabsSlice";
import { config } from "../../../app/utils/config";
import moment from "moment-timezone";

const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const TRANSITION_SPRING = "all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)";

// ---------------------------------------------------------------------------
// Helpers: map PrimeReact Tag severity to MUI Chip color
// ---------------------------------------------------------------------------
function chipColorFromSeverity(severity) {
  switch (severity) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "danger":
      return "error";
    case "info":
      return "info";
    default:
      return "default";
  }
}

// ---------------------------------------------------------------------------
// Shared sx tokens – premium futuristic style
// ---------------------------------------------------------------------------
const drawerPaperSx = {
  borderTopLeftRadius: 24,
  borderTopRightRadius: 24,
  maxHeight: "92vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  bgcolor: "rgba(255,255,255,0.92)",
  backdropFilter: "blur(24px) saturate(1.4)",
  WebkitBackdropFilter: "blur(24px) saturate(1.4)",
  boxShadow:
    "0 -4px 32px rgba(0,0,0,0.08), 0 -1px 8px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.6)",
  border: "1px solid rgba(255,255,255,0.5)",
  borderBottom: "none",
};

const headerSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 3,
  pt: 1.5,
  pb: 2,
  borderBottom: "1px solid rgba(0,0,0,0.05)",
  flexShrink: 0,
  background: "linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 100%)",
};

const bodySx = {
  flex: 1,
  overflowY: "auto",
  px: 3,
  py: 2.5,
  WebkitOverflowScrolling: "touch",
  scrollBehavior: "smooth",
  "&::-webkit-scrollbar": { width: 5 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(0,0,0,0.08)",
    borderRadius: 3,
    "&:hover": { background: "rgba(0,0,0,0.14)" },
  },
};

const footerSx = {
  display: "flex",
  gap: 1.5,
  px: 3,
  py: 2,
  borderTop: "1px solid rgba(0,0,0,0.05)",
  bgcolor: "rgba(255,255,255,0.65)",
  backdropFilter: "blur(20px) saturate(1.3)",
  WebkitBackdropFilter: "blur(20px) saturate(1.3)",
  flexShrink: 0,
  pb: "calc(18px + env(safe-area-inset-bottom, 0px))",
  boxShadow: "0 -2px 16px rgba(0,0,0,0.03)",
};

const fieldRowSx = {
  display: "flex",
  flexDirection: "column",
  gap: 0.75,
  mb: 2.5,
};

const inlineRowSx = {
  display: "flex",
  gap: 1.5,
  alignItems: "center",
  mb: 2,
};

const labelSx = {
  variant: "body2",
  color: "text.secondary",
  fontWeight: 600,
  fontSize: "0.7rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  mb: 0.75,
  opacity: 0.7,
};

const infoRowSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  py: 1.25,
  px: 1,
  borderRadius: "10px",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    bgcolor: "rgba(0,0,0,0.015)",
  },
};

const outlinedButtonSx = {
  minHeight: 50,
  borderRadius: 14,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.925rem",
  letterSpacing: "-0.01em",
  color: "rgba(0,0,0,0.7)",
  border: "1.5px solid rgba(0,0,0,0.12)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    transform: "scale(1.02)",
    color: "rgba(0,0,0,0.85)",
    border: "1.5px solid rgba(0,0,0,0.2)",
    bgcolor: "rgba(0,0,0,0.02)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  },
  "&:active": {
    transform: "scale(0.97)",
    transition: "all 0.1s ease",
  },
};

const containedButtonSx = {
  minHeight: 50,
  borderRadius: 14,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.925rem",
  letterSpacing: "-0.01em",
  bgcolor: "#FF9015",
  color: "#fff",
  boxShadow: "0 2px 8px rgba(255,144,21,0.25), 0 1px 3px rgba(255,144,21,0.12)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    bgcolor: "#e8820e",
    color: "#fff",
    boxShadow: "0 4px 20px rgba(255,144,21,0.4), 0 2px 8px rgba(255,144,21,0.2)",
    transform: "translateY(-1px) scale(1.02)",
  },
  "&:active": {
    bgcolor: "#d4760c",
    transform: "scale(0.97)",
    boxShadow: "0 1px 4px rgba(255,144,21,0.2)",
    transition: "all 0.1s ease",
  },
};

const containedErrorButtonSx = {
  minHeight: 50,
  borderRadius: 14,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.925rem",
  letterSpacing: "-0.01em",
  boxShadow: "0 2px 8px rgba(211,47,47,0.15)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    boxShadow: "0 4px 20px rgba(211,47,47,0.3), 0 2px 8px rgba(211,47,47,0.12)",
    transform: "translateY(-1px) scale(1.02)",
  },
  "&:active": {
    transform: "scale(0.97)",
    boxShadow: "0 1px 4px rgba(211,47,47,0.15)",
    transition: "all 0.1s ease",
  },
};

const textFieldSx = {
  "& textarea": { fontSize: "16px", lineHeight: 1.6 },
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    bgcolor: "rgba(0,0,0,0.02)",
    border: "1.5px solid rgba(0,0,0,0.06)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": { border: "none" },
    "&:hover": {
      bgcolor: "rgba(0,0,0,0.03)",
      border: "1.5px solid rgba(0,0,0,0.1)",
    },
    "&.Mui-focused": {
      bgcolor: "rgba(255,255,255,1)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12), 0 2px 12px rgba(255,144,21,0.06)",
      border: "1.5px solid rgba(255,144,21,0.35)",
    },
  },
};

// ===========================================================================
// Component
// ===========================================================================
export function MobileLeaveFormDrawer(props) {
  const { show, onHide } = props;

  const dispatch = useDispatch();

  // ---- Redux selectors (identical to LeaveFormModal) ----
  const userId = useSelector((state) => state.login.userId);
  const userRoleId = useSelector((state) => state.login.userRoleId);
  const userCollabId = useSelector((state) => state.login.userId);
  const teams = useSelector((state) => state.teams.list);
  const currentCollabs = useSelector((state) => state.collabs.currentList);
  const myTeam = useSelector((state) => state.teams.myTeam);
  const sampleLeaves = useSelector((state) => state.sampleLeaves.list);
  const leaveModalType = useSelector((state) => state.leaves.leaveModalType);
  const selectedLeaveFormData = useSelector((state) => state.leaves.selectedLeaveFormData);
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const collabs = useSelector((state) => state.collabs.list);

  // ---- Local state (identical) ----
  const [formError, setFormError] = useState(null);
  const [selectedCollab, setSelectedCollab] = useState();
  const [formData, setFormData] = useState({});
  const [showToDate, setShowToDate] = useState(false);
  const [leavesOptions, setLeavesOptions] = useState([]);
  const [timezone, setTimezone] = useState(config.timezone);

  const mounted = useRef(false);

  // ---- Effects (identical) ----
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      setNewFormData();
      setSelectedCollab(null);
    }
  });

  useEffect(() => {
    loadLeaves();
  }, [dispatch, collabs]);

  useEffect(() => {
    if (selectedLeaveFormData && teams) {
      let team = teams.find((t) => t.id == selectedLeaveFormData.id_team);
      if (team) {
        switch (team.id_country) {
          case 3:
            //setTimezone("Indian/Mauritius")
            break;
          default:
            break;
        }
      }
    }
  }, [selectedLeaveFormData, teams]);

  // ---- Helper functions (identical) ----
  function setNewFormData() {
    let from = moment().tz(timezone).startOf("day");
    let to = moment().tz(timezone).endOf("day");
    let leaveData = {
      id: generateUniqueId(),
      id_team: null,
      id_collab: null,
      id_sample_leave: null,
      type: null,
      from: parseInt(from.format("x")),
      to: parseInt(to.format("x")),
      description: null,
    };
    setFormData(leaveData);
  }

  function getFromDayPeriodOptions() {
    return [
      { value: 0, label: <Langs str="Matin" /> },
      { value: 12, label: <Langs str="Midi" /> },
    ];
  }

  function getCollabsOptions() {
    let uniqueIds = [];
    let collabsOptions = currentCollabs.filter((collab) => {
      return (
        isCollabPresent(collab.statut) &&
        (!myTeam ||
          checkPermission(config.scopes.leaves, config.permissionTypes.edit, collab.id_team) ||
          collab.id_team != myTeam.id ||
          collab.id == userCollabId) &&
        !collab.id_team_delegate
      );
    });
    return collabsOptions.filter((c) => {
      if (uniqueIds.includes(c.id)) return false;
      uniqueIds.push(c.id);
      return true;
    });
  }

  function isManager() {
    let teamId = formData.id_team;
    if (selectedLeaveFormData && leaveModalType == "UPDATE_LEAVE")
      teamId = selectedLeaveFormData.id_team;
    return checkPermission(config.scopes.leaves, config.permissionTypes.edit, teamId);
  }

  function getFromDayPeriod(value) {
    switch (value) {
      case 0:
        return "Matin";
      case 12:
        return "Midi";
      default:
        break;
    }
  }

  function getToDayPeriodOptions() {
    return [
      { value: 12, label: <Langs str="Midi" /> },
      { value: 23, label: <Langs str="Soir" /> },
    ];
  }

  function getToDayPeriod(value) {
    switch (value) {
      case 12:
        return "Midi";
      case 23:
        return "Soir";
      default:
        break;
    }
  }

  function getSelectedDayPeriod(dateTime) {
    let date = moment(dateTime, "x").tz(timezone);
    let hour = date.hours();
    if (hour < 12) return 0;
    else if (hour > 12) return 23;
    return 12;
  }

  // ---- Event handlers (identical) ----
  const onFromDayPeriodChange = (option) => {
    let fromDate = moment(formData.from, "x").tz(timezone).startOf("day");
    console.log(timezone, option.value);
    console.log(fromDate);
    if (option.value > 0) {
      fromDate.hours(option.value);
    }
    setFormData({ ...formData, from: parseInt(fromDate.format("x")) });
  };

  const onToDayPeriodChange = (option) => {
    let toDate = moment(formData.to)
      .tz(timezone)
      .hour(12)
      .minute(0)
      .second(0)
      .millisecond(0);
    if (option.value > 12) {
      toDate = moment(formData.to).tz(timezone).endOf("day");
    }
    setFormData({ ...formData, to: parseInt(toDate.format("x")) });
  };

  function hide() {
    setNewFormData();
    setSelectedCollab(null);
    onHide();
  }

  function updateLeaveWithStatus(status) {
    dispatch(
      submitLeave({
        id: selectedLeaveFormData.id,
        status: status,
      })
    ).then((response) => {
      if (response["payload"]["responseData"]) {
        loadPlanning();
      }
      if (response["payload"]["responseError"]) {
        if (response["payload"]["responseError"]) {
          if (
            response["payload"]["responseError"]["data"] &&
            response["payload"]["responseError"]["data"]["message"]
          ) {
            showErrorToast(response["payload"]["responseError"]["data"]["message"]);
          } else showErrorToast();
        }
      }
    });
    onHide();
  }

  function loadPlanning() {
    if (selectedTeamsIds && selectedTeamsIds.length && collabs.length) {
      let selectedCollabsIds = collabs.reduce((array, current) => {
        array.push(current.id);
        return array;
      }, []);
      dispatch(getPlanning({ selectedTeamsIds, selectedCollabsIds }));
    }
  }

  function handleRequestDelete() {
    dispatch(requestDeleteLeave({ id: selectedLeaveFormData.id })).then((response) => {
      if (response["payload"]["responseData"]) {
        loadPlanning();
        if (selectedLeaveFormData["status"] != "PENDING")
          showSuccessToast(<Langs str="REQUEST_SENT" />);
      }
      if (response["payload"]["responseError"]) {
        showErrorToast();
      }
    });
    onHide();
  }

  function handleSend() {
    if (formData.from && formData.to && formData.to < formData.from) {
      setFormError("La date de fin ne peut pas être antérieure à la date de début");
      return;
    }
    if (!formData.id_collab) {
      setFormError("id_collab");
      return;
    }
    if (!formData.id_sample_leave) {
      setFormError("id_sample_leave");
      return;
    }
    setFormError(null);
    dispatch(requestLeave(formData)).then(() => {
      dispatch(getCollabs({ selectedTeamsIds, isCurrent: true }));
      hide();
    });
  }

  function loadLeaves() {
    if (selectedTeamsIds && selectedTeamsIds.length) {
      let selectedCollabsIds = collabs.reduce((array, current) => {
        array.push(current.id);
        return array;
      }, []);
      dispatch(getLeaves({ selectedTeamsIds, selectedCollabsIds }));
    }
  }

  function handleCollabChange(collab) {
    let collabTimezone = config.timezone;
    switch (collab.id_country) {
      case 3:
        collabTimezone = "Indian/Mauritius";
        break;
      default:
        break;
    }
    setTimezone(collabTimezone);

    // Work-stoppage types (Maladie, Maternité, Paternité, Décès, Mariage, Naissance, Congé
    // parental) are declared from Arrêts de travail, not requested here.
    let availableLeaves = sampleLeaves.filter(
      (s) => !s.visibility && !s.isWorkStoppage && (!s.id_country || s.id_country == collab.id_country)
    );
    if (!isAdmin(userRoleId)) {
      availableLeaves = availableLeaves.filter((sampleLeave) => sampleLeave.roleId >= 2);
    }
    if (collab.id != userId && !isOwner(userRoleId, collab.id_country)) {
      availableLeaves = availableLeaves.filter((sampleLeave) => sampleLeave.roleId < 3);
    }

    setSelectedCollab(collab);
    setLeavesOptions(availableLeaves);

    let from = moment().tz(collabTimezone).startOf("day");
    let to = moment().tz(collabTimezone).endOf("day");

    setFormData({
      ...formData,
      id_collab: collab.id,
      id_team: collab.id_team,
      id_sample_leave: availableLeaves[0].id,
      type: availableLeaves[0].type,
      from: parseInt(from.format("x")),
      to: parseInt(to.format("x")),
    });
  }

  function handleTypeChange(selectedSample) {
    setFormData({ ...formData, id_sample_leave: selectedSample.id, type: selectedSample.type });
  }

  const handleStartDateChange = (time) => {
    let selectedDate = moment(time, "x").tz(timezone);
    let formDate = moment(formData.from, "x").tz(timezone);
    formDate.year(selectedDate.year());
    formDate.month(selectedDate.month());
    formDate.date(selectedDate.date());
    // The end takes the same day, as on desktop, while it still follows the start (same day
    // as the previous start) or would otherwise end before it. Its half day is kept.
    const previousFrom = moment(formData.from, "x").tz(timezone);
    let toDate = moment(formData.to, "x").tz(timezone);
    if (!formData.to || toDate.isSame(previousFrom, "day") || toDate.isBefore(formDate, "day")) {
      toDate.year(selectedDate.year());
      toDate.month(selectedDate.month());
      toDate.date(selectedDate.date());
    }
    setFormData({ ...formData, from: parseInt(formDate.format("x")), to: parseInt(toDate.format("x")) });
    setShowToDate(true);
  };

  const handleEndDateChange = (time) => {
    let selectedDate = moment(time, "x").tz(timezone);
    let toDate = moment(formData.to, "x").tz(timezone);
    toDate.year(selectedDate.year());
    toDate.month(selectedDate.month());
    toDate.date(selectedDate.date());
    setFormData({ ...formData, to: parseInt(toDate.format("x")) });
    setShowToDate(false);
  };

  const handleDescriptionChange = (event) => {
    setFormData({ ...formData, description: event.target.value });
  };

  function getLeaveLabel(leaveData) {
    if (leaveData.type == "LEAVE") return <Langs str={leaveData.type} />;
    let sampleLeave = sampleLeaves.filter((s) => s.id == leaveData.id_sample_leave);
    if (sampleLeave[0]) return sampleLeave[0].description;
    return "";
  }

  // =========================================================================
  // Render: Header
  // =========================================================================
  function renderHeader() {
    if (selectedLeaveFormData && leaveModalType === "UPDATE_LEAVE") {
      return (
        <Box sx={headerSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, letterSpacing: "-0.025em", fontSize: "1.05rem" }}
              noWrap
            >
              {getLeaveLabel(selectedLeaveFormData)}
            </Typography>
            <Chip
              label={<Langs str={selectedLeaveFormData.status} />}
              color={chipColorFromSeverity(getTagClass(selectedLeaveFormData.status))}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: "0.7rem",
                borderRadius: "8px",
                letterSpacing: "0.03em",
                transition: TRANSITION_SPRING,
                "&:hover": { transform: "scale(1.05)" },
              }}
            />
          </Box>
          <IconButton
            onClick={hide}
            edge="end"
            aria-label="close"
            sx={{
              ml: 1,
              width: 36,
              height: 36,
              borderRadius: "12px",
              bgcolor: "rgba(0,0,0,0.03)",
              transition: TRANSITION_SPRING,
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.07)",
                transform: "scale(1.08) rotate(90deg)",
              },
              "&:active": { transform: "scale(0.92)", transition: "all 0.1s ease" },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      );
    }

    // ADD_LEAVE header
    return (
      <Box sx={headerSx}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, letterSpacing: "-0.025em", fontSize: "1.05rem" }}
        >
          {leaveModalType === "ADD_LEAVE" ? (
            <Langs str={isManager() ? "Add New Leave" : "Request New Leave"} />
          ) : (
            ""
          )}
        </Typography>
        <IconButton
          onClick={hide}
          edge="end"
          aria-label="close"
          sx={{
            width: 36,
            height: 36,
            borderRadius: "12px",
            bgcolor: "rgba(0,0,0,0.03)",
            transition: TRANSITION_SPRING,
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.07)",
              transform: "scale(1.08) rotate(90deg)",
            },
            "&:active": { transform: "scale(0.92)", transition: "all 0.1s ease" },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    );
  }

  // =========================================================================
  // Render: Body - UPDATE_LEAVE
  // =========================================================================
  function renderUpdateBody() {
    if (!selectedLeaveFormData) return null;

    const rows = [
      { label: "COLLAB", value: selectedLeaveFormData.collab },
      {
        label: "START",
        value: (
          <>
            {moment(selectedLeaveFormData.from).tz(timezone).format("DD MMMM YY")}
            {" - "}
            <Langs str={getFromDayPeriod(getSelectedDayPeriod(selectedLeaveFormData.from))} />
          </>
        ),
      },
      {
        label: "END",
        value: (
          <>
            {moment(selectedLeaveFormData.to).tz(timezone).format("DD MMMM YY")}
            {" - "}
            <Langs str={getToDayPeriod(getSelectedDayPeriod(selectedLeaveFormData.to))} />
          </>
        ),
      },
      {
        label: "TYPE",
        value: <Langs str={selectedLeaveFormData.type} />,
      },
    ];

    if (selectedLeaveFormData.description) {
      rows.push({ label: "DESCRIPTION", value: selectedLeaveFormData.description });
    }

    return (
      <Box sx={bodySx}>
        {rows.map((row, idx) => (
          <Box
            key={idx}
            sx={{
              mb: 2.5,
              p: 1.75,
              borderRadius: "14px",
              bgcolor: "rgba(0,0,0,0.02)",
              border: "1px solid rgba(0,0,0,0.04)",
              transition: TRANSITION_SMOOTH,
              "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
            }}
          >
            <Typography sx={{ ...labelSx }}>
              <Langs str={row.label} />
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, letterSpacing: "-0.01em", mt: 0.25 }}
            >
              {row.value}
            </Typography>
          </Box>
        ))}

        {/* Meta info - muted */}
        <Box
          sx={{
            mt: 2.5,
            pt: 2,
            px: 0.5,
            borderTop: "1px solid rgba(0,0,0,0.04)",
          }}
        >
          <Box sx={infoRowSx}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontSize: "0.7rem", letterSpacing: "0.04em", textTransform: "uppercase" }}
            >
              <Langs str="CREATED_AT" />
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 500 }}>
              {getLastUpdatedFromDate(selectedLeaveFormData.createdAt) || "-"}
            </Typography>
          </Box>
          <Box sx={infoRowSx}>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontSize: "0.7rem", letterSpacing: "0.04em", textTransform: "uppercase" }}
            >
              <Langs str="CREATED_BY" />
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 500 }}>
              {selectedLeaveFormData.createdBy}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // =========================================================================
  // Render: Body - ADD_LEAVE
  // =========================================================================
  function renderAddBody() {
    return (
      <Box sx={bodySx}>
        {/* Collab selector */}
        <Box sx={fieldRowSx}>
          <Typography sx={labelSx}>
            <Langs str="COLLAB" />
          </Typography>
          <SelectInput
            value={formData.id_collab}
            onChange={(e) => handleCollabChange(e)}
            error={formError === "id_collab"}
            options={getCollabsOptions()}
          />
        </Box>

        {/* Start date row */}
        <Box sx={fieldRowSx}>
          <Typography sx={labelSx}>
            <Langs str="START" />
          </Typography>
          <Box sx={inlineRowSx}>
            <Box sx={{ flex: 1 }}>
              <FormDatePicker
                value={formData.from ? moment(formData.from, "x").tz(timezone).format("YYYY-MM-DD") : ""}
                onChange={(date) => date && handleStartDateChange(moment.tz(date, "YYYY-MM-DD", timezone).valueOf())}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <SelectInput
                options={getFromDayPeriodOptions()}
                value={getSelectedDayPeriod(formData.from)}
                onChange={onFromDayPeriodChange}
              />
            </Box>
          </Box>
        </Box>

        {/* End date row */}
        <Box sx={fieldRowSx}>
          <Typography sx={labelSx}>
            <Langs str="END" />
          </Typography>
          <Box sx={inlineRowSx}>
            <Box sx={{ flex: 1 }}>
              <FormDatePicker
                value={formData.to ? moment(formData.to, "x").tz(timezone).format("YYYY-MM-DD") : ""}
                minDate={formData.from ? moment(formData.from, "x").tz(timezone).format("YYYY-MM-DD") : undefined}
                onChange={(date) => date && handleEndDateChange(moment.tz(date, "YYYY-MM-DD", timezone).valueOf())}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <SelectInput
                options={getToDayPeriodOptions()}
                value={getSelectedDayPeriod(formData.to)}
                onChange={onToDayPeriodChange}
              />
            </Box>
          </Box>
        </Box>

        {/* Type selector */}
        <Box sx={fieldRowSx}>
          <Typography sx={labelSx}>
            <Langs str="TYPE" />
          </Typography>
          <SelectInput
            value={formData.id_sample_leave}
            onChange={(e) => handleTypeChange(e)}
            error={formError === "id_sample_leave"}
            options={leavesOptions}
          />
        </Box>

        {/* Description */}
        <Box sx={fieldRowSx}>
          <Typography sx={labelSx}>
            <Langs str="DESCRIPTION" />
          </Typography>
          <MuiTextField
            multiline
            minRows={3}
            maxRows={5}
            name="description"
            inputProps={{ maxLength: 200 }}
            value={formData.description || ""}
            onChange={handleDescriptionChange}
            placeholder="..."
            fullWidth
            size="small"
            sx={textFieldSx}
          />
        </Box>

        {/* Leave balance info */}
        {selectedCollab && (
          <Box
            sx={{
              mt: 1.5,
              p: 2,
              borderRadius: "16px",
              bgcolor: "rgba(0,0,0,0.02)",
              border: "1px solid rgba(0,0,0,0.05)",
              boxShadow: "0 1px 6px rgba(0,0,0,0.03)",
              transition: TRANSITION_SMOOTH,
            }}
          >
            {/* CPN-1 */}
            <Box sx={infoRowSx}>
              <Typography variant="body2" color="text.secondary">
                <Langs str="CPN-1" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCollab.solde_cp_old}{" "}
                <Langs str="days available till" />{" "}
                {moment(selectedCollab.solde_cp_old_expiry).tz(timezone).format("DD/MM/YYYY")}
              </Typography>
            </Box>

            {/* CPN */}
            <Box sx={infoRowSx}>
              <Typography variant="body2" color="text.secondary">
                <Langs str="CPN" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCollab.solde_cp_current} <Langs str="days available" />
              </Typography>
            </Box>

            {/* Sick Leaves */}
            {leavesOptions.find((l) => l.type == "S") && (
              <Box sx={infoRowSx}>
                <Typography variant="body2" color="text.secondary">
                  <Langs str="Sick Leaves" />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedCollab.solde_sick_leaves} <Langs str="days available" />
                </Typography>
              </Box>
            )}

            {/* Extra Leaves */}
            {leavesOptions.find((l) => l.type == "EXTRA") && (
              <Box sx={infoRowSx}>
                <Typography variant="body2" color="text.secondary">
                  <Langs str={leavesOptions.find((l) => l.type == "EXTRA")?.description} />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedCollab.solde_extra_leaves} <Langs str="days available" />
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Date validation error */}
        {formData.from && formData.to && formData.to < formData.from && (
          <Alert
            severity="error"
            sx={{
              mt: 2.5,
              borderRadius: "14px",
              border: "1px solid rgba(211,47,47,0.12)",
              boxShadow: "0 2px 12px rgba(211,47,47,0.08)",
              "& .MuiAlert-icon": { opacity: 0.85 },
            }}
          >
            <Langs str="ADD_LEAVE_ERROR_DATE" />
          </Alert>
        )}
      </Box>
    );
  }

  // =========================================================================
  // Render: Body dispatcher
  // =========================================================================
  function renderBody() {
    if (selectedLeaveFormData && leaveModalType === "UPDATE_LEAVE") {
      return renderUpdateBody();
    }
    if (leaveModalType === "ADD_LEAVE") {
      return renderAddBody();
    }
    return null;
  }

  // =========================================================================
  // Render: Footer - Collab (non-admin/manager)
  // =========================================================================
  function renderCollabFooter() {
    switch (leaveModalType) {
      case "UPDATE_LEAVE":
        if (
          selectedLeaveFormData["id_collab"] == userId &&
          !["DELETED", "DELETE_REQUESTED"].includes(selectedLeaveFormData.status)
        )
          return (
            <Box sx={footerSx}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => hide()}
                sx={outlinedButtonSx}
              >
                <Langs str="CANCEL" />
              </Button>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={() => handleRequestDelete()}
                sx={containedErrorButtonSx}
              >
                <Langs
                  str={selectedLeaveFormData["status"] == "PENDING" ? "DELETE" : "REQUEST_DELETE"}
                />
              </Button>
            </Box>
          );
        return null;

      case "ADD_LEAVE":
        return (
          <Box sx={footerSx}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => hide()}
              sx={outlinedButtonSx}
            >
              <Langs str="CANCEL" />
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={() => handleSend()}
              sx={containedButtonSx}
            >
              <Langs str="SEND" />
            </Button>
          </Box>
        );

      default:
        return null;
    }
  }

  // =========================================================================
  // Render: Footer - Admin / Manager
  // =========================================================================
  function renderAdminFooter() {
    switch (leaveModalType) {
      case "UPDATE_LEAVE":
        // PENDING => Reject + Validate
        if (selectedLeaveFormData.status === "PENDING") {
          return (
            <Box sx={footerSx}>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={() => updateLeaveWithStatus("REJECTED")}
                sx={containedErrorButtonSx}
              >
                <Langs str="REJECT" />
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => updateLeaveWithStatus("VALIDATED")}
                sx={containedButtonSx}
              >
                <Langs str="VALIDATE" />
              </Button>
            </Box>
          );
        }

        // Non-DELETED => Cancel + Delete
        if (selectedLeaveFormData.status !== "DELETED") {
          return (
            <Box sx={footerSx}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => hide()}
                sx={outlinedButtonSx}
              >
                <Langs str="CANCEL" />
              </Button>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={() => updateLeaveWithStatus("DELETED")}
                sx={containedErrorButtonSx}
              >
                <Langs str="DELETE" />
              </Button>
            </Box>
          );
        }
        return null;

      case "ADD_LEAVE":
        return (
          <Box sx={footerSx}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => hide()}
              sx={outlinedButtonSx}
            >
              <Langs str="CANCEL" />
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={() => handleSend()}
              sx={containedButtonSx}
            >
              <Langs str={isManager() ? "ADD" : "SEND"} />
            </Button>
          </Box>
        );

      default:
        return null;
    }
  }

  // =========================================================================
  // Render: Footer dispatcher
  // =========================================================================
  function renderFooter() {
    if (isOwner(userRoleId) || isManager(selectedLeaveFormData?.id_team)) {
      return renderAdminFooter();
    }
    return renderCollabFooter();
  }

  // =========================================================================
  // Main render
  // =========================================================================
  return (
    <SwipeableDrawer
      anchor="bottom"
      open={!!show}
      onClose={hide}
      onOpen={() => {}}
      disableSwipeToOpen
      PaperProps={{
        sx: drawerPaperSx,
      }}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {/* Swipe indicator pill */}
      <Box
        sx={{
          width: 36,
          height: 4.5,
          borderRadius: 3,
          bgcolor: "rgba(0,0,0,0.1)",
          mx: "auto",
          mt: 1.25,
          mb: 0.25,
          flexShrink: 0,
          transition: TRANSITION_SMOOTH,
          "&:hover": { bgcolor: "rgba(0,0,0,0.18)", width: 44 },
        }}
      />

      {renderHeader()}
      {renderBody()}
      {renderFooter()}
    </SwipeableDrawer>
  );
}
